import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SearchBarProps {
  placeholder?: string;
  onSearch: (query: string) => void;
  debounceTime?: number;
  className?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search...',
  onSearch,
  debounceTime = 300,
  className = '',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isTyping) {
        onSearch(searchQuery);
        setIsTyping(false);
      }
    }, debounceTime);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceTime, isTyping, onSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setIsTyping(true);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch('');
    setIsTyping(false);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder={placeholder}
          className="w-full pl-10 pr-10"
          value={searchQuery}
          onChange={handleChange}
          aria-label="Search controls"
        />
        {searchQuery && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:bg-transparent hover:text-foreground"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {isTyping && (
        <div className="absolute left-0 right-0 top-full mt-1 text-xs text-muted-foreground animate-in fade-in">
          Searching...
        </div>
      )}
    </div>
  );
};

export default SearchBar;
